/**
 * Custom PricePlannerComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { PricePlannerComponent as ParentPricePlannerComponent } from '@shared-lib/chart-components/price-planner/price-planner.component';

@Component({
  selector: 'app-price-planner',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSliderModule],
  templateUrl: './price-planner.component.html',
  styleUrls: ['./price-planner.component.scss']
})
export class PricePlannerComponent extends ParentPricePlannerComponent {
  // Constructor is inherited from parent to avoid dependency injection issues
  private applyChangesClicked = false;
  private applyRefreshWatchTimer?: ReturnType<typeof setInterval>;
  private sliderRefreshTimer?: ReturnType<typeof setTimeout>;
  private applyDispatchTimer?: ReturnType<typeof setTimeout>;

  get primaryResultRow(): any {
    return this.resultRows?.[0];
  }

  get cardResultRows(): any[] {
    return Array.isArray(this.resultRows) ? this.resultRows.slice(1) : [];
  }

  override onSliderChange(slider: any, rawValue: any): any {
    this.applyChangesClicked = false;
    this.updateSliderStateWithoutRefresh(slider, rawValue);

    if (this.sliderRefreshTimer) {
      clearTimeout(this.sliderRefreshTimer);
    }

    this.sliderRefreshTimer = setTimeout(() => {
      this.sliderRefreshTimer = undefined;
      super.onSliderChange(slider, rawValue);
    }, 300);
  }

  private updateSliderStateWithoutRefresh(slider: any, rawValue: any): void {
    if (!slider?.key) {
      return;
    }

    const min = this.parsePlannerNumber(slider.min, 0);
    const max = this.parsePlannerNumber(slider.max, 100);
    const fallback = this.parsePlannerNumber(
      this.formState[slider.key] ?? slider.value ?? slider.defaultValue,
      min,
    );
    const nextValue = this.boundPlannerNumber(this.parsePlannerNumber(rawValue, fallback), min, max);
    const decimals = this.countPlannerDecimals(slider.step ?? 1);
    this.formState[slider.key] = Number(nextValue.toFixed(decimals));

    if (slider?.key === 'priceChangePercentage') {
      this.formState['set_discount'] = Number.isFinite(nextValue)
        ? Math.abs(nextValue)
        : rawValue;
    }

  }

  override onFieldChange(field: any, rawValue: any): any {
    const normalizedValue = typeof rawValue === 'string'
      ? rawValue.replace(/,/g, '')
      : rawValue;
    return super.onFieldChange(field, normalizedValue);
  }

  isSlidingOut = false;

  override dismiss(): any {
    if (this.isSlidingOut) return;
    this.isSlidingOut = true;

    const widgetData = this.data as any;
    
    // Wait for the slide-out animation to complete (300ms) before changing the layout and dismissing
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('pat-price-planner-close', {
        detail: {
          chartWidgetId: widgetData?.chartConfig?.plannerPanel?.chartWidgetId || widgetData?.plannerPanel?.chartWidgetId,
          plannerWidgetId: widgetData?.widgetId || 'widget-price-planner',
        },
      }));
      super.dismiss();
      this.isSlidingOut = false;
    }, 300);
  }

  override submitPlanner(): any {
    this.applyChangesClicked = true;
    this.clearSliderRefreshTimer();
    this.clearApplyRefreshWatch();
    const result = super.submitPlanner();
    this.watchApplyCompletionAndRefresh();
    return result;
  }

  override ngOnDestroy(): void {
    this.clearSliderRefreshTimer();
    this.clearApplyRefreshWatch();
    this.clearApplyDispatchTimer();
    super.ngOnDestroy();
  }

  override getSliderDisplayValue(slider: any): any {
    // TODO: Add your custom logic here
    return super.getSliderDisplayValue(slider);
  }

  override getSliderMinLabel(slider: any): any {
    // TODO: Add your custom logic here
    return super.getSliderMinLabel(slider);
  }

  override getSliderMaxLabel(slider: any): any {
    // TODO: Add your custom logic here
    return super.getSliderMaxLabel(slider);
  }

  override getFieldInputType(field: any): any {
    return field?.type === 'number' || field?.type === 'currency' ? 'text' : super.getFieldInputType(field);
  }

  override getFieldDisplayValue(field: any): any {
    return super.getFieldDisplayValue(field);
  }

  override getEditableFieldValue(field: any): any {
    const value = super.getEditableFieldValue(field);
    return field?.type === 'number' || field?.type === 'currency'
      ? this.formatPlannerNumber(value, field?.decimals ?? 0, field?.prefix, field?.suffix)
      : value;
  }

  override getResultValue(row: any): any {
    // TODO: Add your custom logic here
    return super.getResultValue(row);
  }

  override getResultStyles(row: any): any {
    // TODO: Add your custom logic here
    return super.getResultStyles(row);
  }

  override getActionButtonStyles(): any {
    if (this.applyChangesClicked) {
      return {
        ...super.getActionButtonStyles(),
        background: '#16a34a',
        color: '#ffffff'
      };
    }

    return super.getActionButtonStyles();
  }

  getActionButtonLabel(): string {
    if (this.applyChangesClicked && !this.submitting) {
      return 'Saved Changes';
    }
    return this.submitting ? 'Applying...' : this.buttonLabel;
  }

  private watchApplyCompletionAndRefresh(): void {
    const startedAt = Date.now();
    this.applyRefreshWatchTimer = setInterval(() => {
      if (this.submitErrorMessage) {
        this.clearApplyRefreshWatch();
        return;
      }

      if (this.submitMessage && !this.submitting) {
        this.clearApplyRefreshWatch();
        this.dispatchPlannerAppliedAfterRefresh();
        return;
      }

      if (Date.now() - startedAt > 15000) {
        this.clearApplyRefreshWatch();
      }
    }, 50);
  }

  private clearApplyRefreshWatch(): void {
    if (this.applyRefreshWatchTimer) {
      clearInterval(this.applyRefreshWatchTimer);
      this.applyRefreshWatchTimer = undefined;
    }
  }

  private dispatchPlannerAppliedAfterRefresh(): void {
    this.clearApplyDispatchTimer();
    this.applyDispatchTimer = setTimeout(() => {
      this.applyDispatchTimer = undefined;
      window.dispatchEvent(new CustomEvent('pat-price-planner-applied', {
        detail: {
          sourceWidgetId: (this.data as any)?.widgetId || 'widget-price-planner',
          listenerWidgets: [
            'widget-grouped-bar-chart-with-status-table',
            'widget-number-stats-n-pat-summary'
          ]
        },
      }));
    }, 900);
  }

  private clearApplyDispatchTimer(): void {
    if (this.applyDispatchTimer) {
      clearTimeout(this.applyDispatchTimer);
      this.applyDispatchTimer = undefined;
    }
  }

  private clearSliderRefreshTimer(): void {
    if (this.sliderRefreshTimer) {
      clearTimeout(this.sliderRefreshTimer);
      this.sliderRefreshTimer = undefined;
    }
  }

  private parsePlannerNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private boundPlannerNumber(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private countPlannerDecimals(value: number): number {
    const text = String(value ?? 1);
    return text.includes('.') ? text.split('.')[1].length : 0;
  }

  private formatPlannerNumber(rawValue: unknown, decimals = 0, prefix = '', suffix = ''): string {
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) {
      return rawValue === undefined || rawValue === null ? '' : String(rawValue);
    }

    return `${prefix || ''}${numericValue.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}${suffix || ''}`;
  }
}
